"use client";

import Link from "next/link";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import { COMPETITORS, type Competitor } from "@/lib/competitors";

export default function CompareClient({ competitor }: { competitor: Competitor }) {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aureliusco.in" },
      { "@type": "ListItem", position: 2, name: "Compare", item: "https://www.aureliusco.in/pricing" },
      { "@type": "ListItem", position: 3, name: `vs ${competitor.name}`, item: `https://www.aureliusco.in/compare/${competitor.slug}` },
    ],
  };

  return (
    <>
      <Navbar />
      <div style={{ background: "#0A0908", minHeight: "100vh", color: "#F5F0E8" }}>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        <main className="max-w-3xl mx-auto px-4 sm:px-8" aria-label={`AureliusCo vs ${competitor.name}`} style={{ paddingTop: "clamp(5rem, 10vw, 8rem)", paddingBottom: "clamp(4rem, 8vw, 6rem)" }}>
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] mb-4 flex items-center justify-center gap-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
              <span className="h-px w-5 inline-block" style={{ background: "#C9A84C" }} />
              Compare
              <span className="h-px w-5 inline-block" style={{ background: "#C9A84C" }} />
            </p>
            <h1 style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "clamp(2.2rem, 5vw, 3.5rem)", fontWeight: 400, lineHeight: 1.1, color: "#F5F0E8" }}>
              AureliusCo vs {competitor.name}
            </h1>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {competitor.name} is {competitor.tagline.charAt(0).toLowerCase() + competitor.tagline.slice(1)}. Here&apos;s how it compares to AureliusCo&apos;s Instagram-only, account-and-reply-priced automation.
            </p>
          </div>

          {/* Pricing */}
          <section className="mb-14" aria-labelledby="pricing-heading">
            <h2 id="pricing-heading" className="mb-4" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400, color: "#F5F0E8" }}>
              Pricing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-6" style={{ border: "1px solid rgba(201,168,76,0.25)", background: "rgba(201,168,76,0.03)" }}>
                <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>AureliusCo</p>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif" }}>
                  Basic $17/mo (1 account, 10 replies/day), Growth $41/mo (3 accounts, 500 replies/day), Elite $66/mo (10 accounts, 2,000 replies/day). 14-day free trial on every plan, annual billing saves 20%.
                </p>
              </div>
              <div className="p-6" style={{ border: "1px solid rgba(245,240,232,0.1)" }}>
                <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(245,240,232,0.5)", fontFamily: "'Montserrat', sans-serif" }}>{competitor.name}</p>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif" }}>
                  {competitor.pricingSummary}
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              <strong style={{ color: "rgba(245,240,232,0.55)" }}>Pricing model:</strong> {competitor.pricingModel}
            </p>
          </section>

          {/* Channels */}
          <section className="mb-14" aria-labelledby="channels-heading">
            <h2 id="channels-heading" className="mb-3" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400, color: "#F5F0E8" }}>
              Channels
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              AureliusCo automates Instagram DMs and comments only, built on Instagram&apos;s official Graph API. {competitor.name} covers: {competitor.channels}.
            </p>
          </section>

          {/* Strengths / Tradeoffs */}
          <section className="mb-14" aria-labelledby="tradeoffs-heading">
            <h2 id="tradeoffs-heading" className="mb-4" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400, color: "#F5F0E8" }}>
              Where {competitor.name} is strong
            </h2>
            <ul className="space-y-2 mb-8">
              {competitor.strengths.map((s) => (
                <li key={s} className="flex items-start gap-3 text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  <span className="mt-0.5 flex-shrink-0" style={{ color: "rgba(245,240,232,0.4)" }}>✓</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <h2 className="mb-4" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400, color: "#F5F0E8" }}>
              Trade-offs to know
            </h2>
            <ul className="space-y-2">
              {competitor.tradeoffs.map((t) => (
                <li key={t} className="flex items-start gap-3 text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  <span className="mt-0.5 flex-shrink-0" style={{ color: "rgba(201,168,76,0.5)" }}>–</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Best for / Aurelius advantage */}
          <section className="mb-16 p-6" style={{ border: "1px solid rgba(201,168,76,0.09)", background: "rgba(255,255,255,0.018)" }} aria-labelledby="verdict-heading">
            <h2 id="verdict-heading" className="mb-4" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.4rem", fontWeight: 400, color: "#F5F0E8" }}>
              Which one fits you
            </h2>
            <p className="text-xs leading-relaxed mb-4" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              <strong style={{ color: "#C9A84C" }}>{competitor.name} is best for:</strong> {competitor.bestFor}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              <strong style={{ color: "#C9A84C" }}>AureliusCo is different because:</strong> {competitor.aureliusAdvantage}
            </p>
          </section>

          <div className="text-center">
            <Link
              href="/pricing"
              className="inline-block px-8 py-3.5 text-xs font-medium tracking-widest uppercase transition-all duration-300"
              style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
            >
              See AureliusCo Pricing →
            </Link>
          </div>

          {COMPETITORS.length > 1 && (
            <div className="mt-16 text-center text-xs" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
              Also comparing:{" "}
              {COMPETITORS.filter((c) => c.slug !== competitor.slug).map((c, i, arr) => (
                <span key={c.slug}>
                  <Link href={`/compare/${c.slug}`} className="underline" style={{ color: "rgba(201,168,76,0.8)" }}>
                    vs {c.name}
                  </Link>
                  {i < arr.length - 1 ? " · " : ""}
                </span>
              ))}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </>
  );
}
